import { Table as AntTable } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { sortBy } from 'lodash';
import moment from 'moment';
import React, { FC } from 'react';
import { TaskId } from 'api/models';
import { DueDate } from 'components/atoms/due-date';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { squad, squadToOptions } from 'components/molecules/squad-picker/utils';
import { TaskNameCell } from 'components/molecules/tasks/task-name-cell';
import { Task } from 'data/tasks';
import { useIsResponsive } from 'hooks/use-media-query';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import styles from './resource-list-table.module.css';

interface Props {
  size: SizeType;
  data: Task[];
  renderExtraActions?: (data) => React.ReactNode;
}

export const TaskTable: FC<Props> = (props) => {
  const { data, size, renderExtraActions } = props;
  const isResponsive = useIsResponsive();

  const navigateToTask = useNavigateToTask();

  const onTaskNameClick = (taskId: TaskId) => {
    if (!isResponsive) {
      navigateToTask(taskId);
    }
  };
  const assigneeLabel = useTranslation('assigned_to');
  const COLUMNS = [
    {
      title: useTranslation('task_name'),
      className: styles.task_name,
      dataIndex: 'taskName',
      key: 'taskName',
      render: (text, record: Task) => {
        return (
          <TaskNameCell
            disableStatusDropdown
            size={size}
            taskName={record.taskName}
            fields={record.fields}
            atRisk={record.atRisk}
            progress={record.progress}
            taskId={TaskId(
              (record as any)?.project?.projectId ?? record.project,
              record.taskId,
            )}
            taskType={record.type}
            onTaskNameClick={onTaskNameClick}
            projectId={(record as any)?.project?.projectId ?? record.project}
          />
        );
      },
    },
    !isResponsive
      ? {
          title: assigneeLabel,
          width: '140px',
          render: (value, record: Task) => {
            return (
              <Trigger
                data-email-template-nowrap
                value={squadToOptions(squad(record.assignee))}
                size="small"
              />
            );
          },
        }
      : {},
    {
      title: useTranslation('due_date'),
      width: '140px',
      render: (_text, record: Task) => {
        return (
          <div className={styles.cell} data-email-template-nowrap>
            <span>
              {record.dueDate ? (
                <DueDate
                  timestamp={moment(record.dueDate)}
                  completed={Boolean(record.completedAt)}
                />
              ) : null}
            </span>
            {renderExtraActions && !isResponsive
              ? renderExtraActions(record)
              : null}
          </div>
        );
      },
    },
  ];

  return (
    <AntTable
      size={size}
      rowKey="taskId"
      className={styles.table}
      rowClassName={(record) => {
        return `${styles.row} ${
          record.private ? `${styles['private-row']} task--is-private` : ''
        }`;
      }}
      pagination={false}
      columns={COLUMNS}
      dataSource={sortBy(data, 'dueDate')}
    />
  );
};
